import React, { ElementType, useEffect, useMemo } from 'react'
import {
  Divider,
  DividerProps,
  Typography,
  TypographyProps,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import {
  Options as ContentfulRichTextOptions,
  documentToReactComponents,
} from '@contentful/rich-text-react-renderer'
import {
  BLOCKS as BASEBLOCKS,
  INLINES,
  MARKS,
} from '@contentful/rich-text-types'
import isEqual from 'lodash/isEqual'
import isString from 'lodash/isString'
import merge from 'lodash/merge'
import useBrand from '../../hooks/useBrand'
import useDiscountToken from '../../hooks/useDiscountToken'
import useHandlebarsContext from '../../hooks/useHandlebarsContext'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import { BindToFields, CssEntry } from '../../types/contentfulAttributes'
import { CustomElementMap, ReferenceType } from '../../types/references'
import { createContentfulClassGetter } from '../../utils/contentful'
import insertHandlebarsTemplate from '../../utils/insertHandlebarsTemplate'
import Link, { LinkProps } from '../Link'
import JumpLink from '../Link/JumpLink'
import MediaModal from '../Media/MediaModal'
import EmbeddedEntry, { findReference } from './EmbeddedEntry'
import RichTextErrorBoundary from './ErrorBoundary'

enum TABLEBLOCKS {
  TableHeaderCell = 'table-header-cell',
}

export const BLOCKS = {
  ...BASEBLOCKS,
  ...TABLEBLOCKS,
}

export type RichTextElementProps = {
  [BLOCKS.HEADING_1]: TypographyProps<ElementType>
  [BLOCKS.HEADING_2]: TypographyProps<ElementType>
  [BLOCKS.HEADING_3]: TypographyProps<ElementType>
  [BLOCKS.HEADING_4]: TypographyProps<ElementType>
  [BLOCKS.HEADING_5]: TypographyProps<ElementType>
  [BLOCKS.HEADING_6]: TypographyProps<ElementType>
  [BLOCKS.OL_LIST]: React.DetailedHTMLProps<
    React.OlHTMLAttributes<HTMLOListElement>,
    HTMLOListElement
  >
  [BLOCKS.UL_LIST]: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLUListElement>,
    HTMLUListElement
  >
  [BLOCKS.LIST_ITEM]: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLLIElement>,
    HTMLLIElement
  >
  [BLOCKS.QUOTE]: React.HTMLProps<HTMLQuoteElement>
  [BLOCKS.HR]: DividerProps
  [BLOCKS.PARAGRAPH]: TypographyProps<ElementType>
  [BLOCKS.TableHeaderCell]: React.HTMLProps<HTMLTableCellElement>
  [INLINES.HYPERLINK]: Omit<LinkProps, 'href' | 'to'>
  [INLINES.ASSET_HYPERLINK]: Omit<LinkProps, 'href' | 'to'>
  [INLINES.ENTRY_HYPERLINK]: Omit<LinkProps, 'href' | 'to'> &
    Partial<{
      open: boolean
      handleClick: () => void
    }>
}

export type RichTextOptions = ContentfulRichTextOptions & {
  customElements?: Partial<CustomElementMap>
  props?: Partial<RichTextElementProps>
}

export interface RichTextProps {
  rawBody: string
  references?: ReferenceType[]
  options?: RichTextOptions
  cssEntries?: CssEntry[] | null
  replace?: Record<string, unknown>
}

const RichText: React.FC<RichTextProps> = (props) => {
  const { rawBody, options, references, cssEntries } = props

  const elementProps = merge(
    {},
    defaultRichTextPropOptions,
    options?.props ?? {}
  ) as RichTextElementProps

  const handlebarsContext = useHandlebarsContext()

  const parsedRawBody = insertHandlebarsTemplate(rawBody, {
    ...handlebarsContext,
    ...(props?.replace && props?.replace),
  })
  const doc = JSON.parse(parsedRawBody ?? rawBody)

  const applyContentfulStyles = createContentfulClassGetter(cssEntries)

  const { classes, cx } = useStyles()
  const { blogPrefix, urlPathBuilder } = useUrlPathBuilder()
  const discountToken = useDiscountToken()
  const brand = useBrand()
  const baseOptions = useMemo(
    () =>
      Object.freeze<ContentfulRichTextOptions>({
        renderNode: {
          [BLOCKS.HEADING_1]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <Typography
                {...elementProps[BLOCKS.HEADING_1]}
                className={cx(
                  elementProps[BLOCKS.HEADING_1].className,
                  applyContentfulStyles(BindToFields.RichTextH1)
                )}
              >
                {children}
              </Typography>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.HEADING_2]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <Typography
                {...elementProps[BLOCKS.HEADING_2]}
                className={cx(
                  elementProps[BLOCKS.HEADING_2].className,
                  applyContentfulStyles(BindToFields.RichTextH2)
                )}
              >
                {children}
              </Typography>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.HEADING_3]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <Typography
                {...elementProps[BLOCKS.HEADING_3]}
                className={cx(
                  elementProps[BLOCKS.HEADING_3].className,
                  applyContentfulStyles(BindToFields.RichTextH3)
                )}
              >
                {children}
              </Typography>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.HEADING_4]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <Typography
                {...elementProps[BLOCKS.HEADING_4]}
                className={cx(
                  elementProps[BLOCKS.HEADING_4].className,
                  applyContentfulStyles(BindToFields.RichTextH4)
                )}
              >
                {children}
              </Typography>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.HEADING_5]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <Typography
                {...elementProps[BLOCKS.HEADING_5]}
                className={cx(
                  elementProps[BLOCKS.HEADING_5].className,
                  applyContentfulStyles(BindToFields.RichTextH5)
                )}
              >
                {children}
              </Typography>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.HEADING_6]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <Typography
                {...elementProps[BLOCKS.HEADING_6]}
                className={cx(
                  elementProps[BLOCKS.HEADING_6].className,
                  applyContentfulStyles(BindToFields.RichTextH6)
                )}
              >
                {children}
              </Typography>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.PARAGRAPH]: (node, children) => {
            // If there are empty lines, convert them to <br />
            if (
              Array.isArray(children) &&
              (children.length === 0 ||
                (children.length === 1 && !children?.[0]))
            ) {
              return <br className={classes.br} />
            }

            let childNodes = null

            // Convert the paragraph line breaks (\n) to <br /> tags
            if (Array.isArray(children)) {
              childNodes = children.flatMap((child) => {
                if (typeof child === 'string') {
                  return child.split('\n').flatMap((text, i) => {
                    if (i > 0) {
                      return [<br key={i} />, text]
                    }

                    return text
                  })
                }

                return child
              })
            }

            return (
              <RichTextErrorBoundary node={node}>
                <Typography
                  {...elementProps[BLOCKS.PARAGRAPH]}
                  className={cx(elementProps[BLOCKS.PARAGRAPH].className, {
                    [classes.p]: brand.name !== 'ZipJob',
                  })}
                >
                  {childNodes}
                </Typography>
              </RichTextErrorBoundary>
            )
          },
          [BLOCKS.HR]: (node) => (
            <RichTextErrorBoundary node={node}>
              <Divider {...elementProps[BLOCKS.HR]} />
            </RichTextErrorBoundary>
          ),
          [BLOCKS.OL_LIST]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <ol
                {...elementProps[BLOCKS.OL_LIST]}
                className={cx(elementProps[BLOCKS.OL_LIST].className, {
                  [classes.ol]: brand.name !== 'ZipJob',
                })}
              >
                {children}
              </ol>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.UL_LIST]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <ul
                {...elementProps[BLOCKS.UL_LIST]}
                className={cx(elementProps[BLOCKS.UL_LIST].className, {
                  [classes.ul]: brand.name !== 'ZipJob',
                })}
              >
                {children}
              </ul>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.LIST_ITEM]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <li
                {...elementProps[BLOCKS.LIST_ITEM]}
                className={cx(elementProps[BLOCKS.LIST_ITEM].className, {
                  [classes.li]: brand.name !== 'ZipJob',
                })}
              >
                {children}
              </li>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.QUOTE]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <blockquote {...elementProps[BLOCKS.QUOTE]}>
                {children}
              </blockquote>
            </RichTextErrorBoundary>
          ),
          [BLOCKS.EMBEDDED_ENTRY]: (node, children) => (
            <EmbeddedEntry
              customElements={options?.customElements ?? {}}
              node={node}
              children={children}
              references={references || []}
            />
          ),
          [BLOCKS.EMBEDDED_ASSET]: (node, children) => (
            <EmbeddedEntry
              customElements={options?.customElements ?? {}}
              node={node}
              children={children}
              references={references || []}
            />
          ),
          [BLOCKS.TableHeaderCell]: (node, children) => (
            <RichTextErrorBoundary node={node}>
              <td
                className={cx(elementProps[BLOCKS.TableHeaderCell].className)}
              >
                {children}
              </td>
            </RichTextErrorBoundary>
          ),
          [INLINES.HYPERLINK]: (node, children) => {
            let href = node.data.uri

            if (
              isString(href) &&
              ['http', 'tel', 'mailto'].every(
                (protocol) => !href.startsWith(protocol)
              ) &&
              !href.startsWith('#')
            ) {
              href = urlPathBuilder([href])
            }

            return (
              <RichTextErrorBoundary node={node}>
                <Link href={href} {...elementProps[INLINES.HYPERLINK]}>
                  {children}
                </Link>
              </RichTextErrorBoundary>
            )
          },
          [INLINES.ASSET_HYPERLINK]: (node, children) => {
            return (
              <RichTextErrorBoundary node={node}>
                <Link
                  href={node.data.uri}
                  {...elementProps[INLINES.ASSET_HYPERLINK]}
                >
                  {children}
                </Link>
              </RichTextErrorBoundary>
            )
          },
          [INLINES.ENTRY_HYPERLINK]: (node, children) => {
            const id = node?.data?.target?.sys?.id

            if (!id) {
              return children
            }

            const ref = findReference(id, references) as any

            if (!ref || !ref.__typename) {
              return children
            }

            if (ref.__typename === 'ContentfulJumpLink') {
              return (
                <RichTextErrorBoundary node={node}>
                  <JumpLink
                    hash={ref.hash}
                    {...elementProps[INLINES.ENTRY_HYPERLINK]}
                  >
                    {children}
                  </JumpLink>
                </RichTextErrorBoundary>
              )
            }

            if (ref.__typename === 'ContentfulMediaInsert') {
              let asset = ''
              if (ref.assetUrl) {
                asset = ref.assetUrl
              } else if (ref.assetFile) {
                asset = ref.assetFile.file.url
              }
              const mediaSchema = {
                name: ref.schemaName,
                description: ref.schemaDescription,
                thumbnailUrl: ref.thumbnail?.file.url,
                uploadDate: ref.createdAt,
                contentUrl: asset,
                transcript: ref.transcript?.transcript ?? null,
              }
              return (
                <RichTextErrorBoundary node={node}>
                  <Typography
                    className={elementProps[INLINES.ENTRY_HYPERLINK].className}
                    onClick={elementProps[INLINES.ENTRY_HYPERLINK].handleClick}
                  >
                    {children}
                  </Typography>
                  <MediaModal
                    asset={asset}
                    open={!!elementProps[INLINES.ENTRY_HYPERLINK].open}
                    onClose={elementProps[INLINES.ENTRY_HYPERLINK]?.handleClick}
                    mediaSchema={mediaSchema}
                  />
                </RichTextErrorBoundary>
              )
            }

            if (
              ref.__typename !== 'ContentfulPost' &&
              ref.__typename !== 'ContentfulLandingPage' &&
              ref.__typename !== 'ContentfulPostResumeSample'
            ) {
              return children
            }

            return (
              <RichTextErrorBoundary node={node}>
                <Link
                  to={urlPathBuilder(
                    ref.__typename.includes('ContentfulPost')
                      ? [blogPrefix, ref.slug]
                      : [ref.slug]
                  )}
                  {...elementProps[INLINES.ENTRY_HYPERLINK]}
                >
                  {children}
                </Link>
              </RichTextErrorBoundary>
            )
          },
          [INLINES.EMBEDDED_ENTRY]: (node, children) => (
            <EmbeddedEntry
              customElements={options?.customElements ?? {}}
              node={node}
              children={children}
              references={references || []}
            />
          ),
        },
        renderMark: {
          [MARKS.BOLD]: (text) => <strong>{text}</strong>,
          [MARKS.ITALIC]: (text) => <em>{text}</em>,
        },
      }),
    /* eslint-disable react-hooks/exhaustive-deps */
    [
      options,
      elementProps,
      blogPrefix,
      references,
      urlPathBuilder,
      discountToken,
    ]
  )

  // Check to see if the developer is confused about how the options work.
  useEffect(() => {
    if (options) {
      warnAboutOptionIssues(options)
    }
  }, [options])

  return <>{documentToReactComponents(doc, merge({}, baseOptions, options))}</>
}

const warnAboutOptionIssues = (options: RichTextOptions) => {
  if (options.props && options.renderNode) {
    const renderNodeSet = new Set(Object.keys(options.renderNode))
    const intersection = new Set(
      Object.keys(options.props).filter((k) => renderNodeSet.has(k))
    )

    if (intersection.size > 0) {
      /* eslint-disable-next-line no-console */
      console.error(
        `RichText.options was provided conflicting keys for props and renderNode. If a component is overridden by renderNode, props for that element will not be applied.`,
        { conflictingKeys: intersection }
      )
    }
  }
}

const defaultRichTextPropOptions: RichTextElementProps = Object.freeze({
  [BLOCKS.HEADING_1]: {
    variant: 'h1',
  },
  [BLOCKS.HEADING_2]: {
    variant: 'h2',
  },
  [BLOCKS.HEADING_3]: {
    variant: 'h3',
  },
  [BLOCKS.HEADING_4]: {
    variant: 'h4',
  },
  [BLOCKS.HEADING_5]: {
    variant: 'h5',
  },
  [BLOCKS.HEADING_6]: {
    variant: 'h6',
  },
  [BLOCKS.OL_LIST]: {},
  [BLOCKS.UL_LIST]: {},
  [BLOCKS.LIST_ITEM]: {},
  [BLOCKS.QUOTE]: {},
  [BLOCKS.HR]: {
    variant: 'middle',
  },
  [BLOCKS.PARAGRAPH]: {
    variant: 'body1',
  },
  [BLOCKS.TableHeaderCell]: {},
  [INLINES.HYPERLINK]: {},
  [INLINES.ENTRY_HYPERLINK]: {},
  [INLINES.ASSET_HYPERLINK]: {},
})

const useStyles = makeStyles()({
  br: {
    '&:last-child': {
      display: 'none',
    },
  },
  li: {
    margin: '.375rem 0',
  },
  p: {
    margin: '.375rem 0',
  },
  ol: {
    margin: '.25rem 0',
    paddingInlineStart: '1.2rem',
  },
  ul: {
    margin: '.25rem 0',
    paddingInlineStart: '1.2rem',
  },
})

// memozing this because we are doing a JSON.parse in RichText and that can slow
// down the UI if it has to run over and over because a parent changed.
export default React.memo(RichText, isEqual)
